// src/context/ForgotPassword.jsx
import { useState, createContext, useMemo } from "react";
import ResendOTP from "../OTPInput/Otp";
import Recovered from "../recovered/recovered";
import Reset from "../reset/Reset";
import LoginForgot from "../../pages/LoginForgot/LoginForgot";
import { axiosInstance } from "../../config";

export const RecoveryContext = createContext();

export default function ForgotPassword() {
  const [page, setPage] = useState("login"); // 'login' -> 'otp' -> 'reset' -> 'recovered'
  const [email, setEmail] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);

  // We do NOT keep the OTP in state.
  // Only keep server artifacts like a resetToken and optional metadata.
  const [recoveryInfo, setRecoveryInfo] = useState({
    email: "",
    resetToken: null,     // issued by backend after successful OTP verification
    otpExpiresAt: null,   // optional: backend can return an absolute timestamp (ms)
  });

  // ---- Actions that talk to backend ----

  // 1) Ask server to generate & send OTP to the email
  const requestOTP = async (emailInput) => {
    setError(null);
    setIsFetching(true);
    try {
      const { data } = await axiosInstance.post("/auth/forgot", { email: emailInput });
      // optional: backend can return { maskedEmail, otpExpiresAt }
      setEmail(emailInput);
      setRecoveryInfo((prev) => ({
        ...prev,
        email: emailInput,
        otpExpiresAt: data?.otpExpiresAt || null,
      }));
      setPage("otp");
    } catch (e) {
      setError(e?.response?.data?.error || "Could not start recovery.");
    } finally {
      setIsFetching(false);
    }
  };

  // 2) Verify OTP with server; server returns a short-lived resetToken (JWT or random)
  const verifyOTP = async (code) => {
    setError(null);
    setIsFetching(true);
    try {
      const { data } = await axiosInstance.post("/auth/verify-otp", {
        email,
        code,
      });
      // Expect { resetToken, expiresInSec? }
      if (!data?.resetToken) throw new Error("Missing reset token from server.");
      setRecoveryInfo((prev) => ({ ...prev, resetToken: data.resetToken }));
      setPage("reset");
    } catch (e) {
      setError(e?.response?.data?.error || "Invalid or expired code.");
    } finally {
      setIsFetching(false);
    }
  };

  // 3) Reset password using resetToken
  const resetPassword = async (newPassword) => {
    setError(null);
    setIsFetching(true);
    try {
      await axiosInstance.post("/auth/reset", {
        resetToken: recoveryInfo.resetToken,
        password: newPassword,
      });
      setPage("recovered");
    } catch (e) {
      setError(e?.response?.data?.error || "Could not reset password.");
    } finally {
      setIsFetching(false);
    }
  };

  // Route to the correct step component
  function NavigateComponents() {
    if (page === "login") return <LoginForgot />;
    if (page === "otp") return <ResendOTP />;
    if (page === "reset") return <Reset />;
    return <Recovered />;
  }

  const containerStyle = {
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };

  const ctxValue = useMemo(
    () => ({
      page,
      setPage,

      // identity + metadata
      email,
      setEmail,
      recoveryInfo,
      setRecoveryInfo,

      // network state
      isFetching,
      setIsFetching,
      error,
      setError,

      // secure flows
      requestOTP,
      verifyOTP,
      resetPassword,
    }),
    [page, email, recoveryInfo, isFetching, error]
  );

  return (
    <RecoveryContext.Provider value={ctxValue}>
      <div style={containerStyle}>
        <NavigateComponents />
      </div>
    </RecoveryContext.Provider>
  );
}
